import { action, computed, observable } from 'mobx'
import type { FilingStatus } from '@taxfyle/txf-api-client'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { validationContext, required, pattern } from 'validx'
import {
  mustBeValidPersonalTaxpayerIdentificationNumber,
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  mustBeInThePast,
  validateObject,
  isValidUsPhoneNumber,
  when,
} from '@/utils/validx-util'
import { AddressFieldSetViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/AddressFieldSet'
import type { TaxPayerProps } from '@/features/jobs/details/tax-type/tax-prep-organizer/models'
import { Id } from '@/infra/id'
import { isNotNil } from '@/utils/object-util'
import { formatFullName } from '@/utils/full-name-util'

export class TaxpayerAccordionViewModel implements ActivationLifecycle {
  readonly lifecycle = new LifecycleTracker()

  @observable accessor isExpanded = false
  @observable accessor id = Id.none
  @observable accessor firstName = ''
  @observable accessor middleInitial = ''
  @observable accessor lastName = ''
  @observable accessor tin = ''
  @observable accessor dateOfBirth: Date | null = null
  @observable accessor occupation = ''
  @observable accessor filingStatus: FilingStatus | null = null
  @observable accessor phoneNumber = ''
  @observable accessor emailAddress = ''

  readonly address = new AddressFieldSetViewModel(false)

  readonly validation = validationContext(this, {
    firstName: [
      required('First name is required'),
      mustBeValidTaxFormInputValue(),
    ],
    lastName: [
      required('Last name is required'),
      mustBeValidTaxFormInputValue(),
    ],
    middleInitial: [
      mustHaveMaxLength(1, 'Middle initial must be a single character'),
      mustBeValidTaxFormInputValue(),
    ],
    tin: [
      required('SSN is required'),
      mustBeValidPersonalTaxpayerIdentificationNumber(),
    ],
    dateOfBirth: [
      required('Date of birth is required'),
      mustBeInThePast('Date of birth must be in the past'),
    ],
    filingStatus: [required('Filing status is required')],
    emailAddress: [
      when(
        () => this.emailAddress.length > 0,
        pattern('email', 'Must be a valid email address'),
      ),
    ],
    phoneNumber: [isValidUsPhoneNumber()],
    address: [validateObject()],
  })

  @computed
  get fullName() {
    return formatFullName(this.firstName, this.middleInitial, this.lastName)
  }

  toProps(): TaxPayerProps {
    return {
      id: this.id || Id.none,
      firstName: this.firstName || null,
      middleInitial: this.middleInitial || null,
      lastName: this.lastName || null,
      tin: this.tin || null,
      dob: this.dateOfBirth,
      occupation: this.occupation || null,
      filingStatus: this.filingStatus,
      phoneNumber: this.phoneNumber || null,
      emailAddress: this.emailAddress || null,
      contactAddress: this.address.toProps(),
    }
  }

  @action.bound
  toggleExpanded() {
    this.isExpanded = !this.isExpanded
  }

  @action.bound
  expand() {
    this.isExpanded = true
  }

  @action.bound
  reset(data: TaxPayerProps | null) {
    this.id = data?.id
    this.firstName = data?.firstName ?? ''
    this.middleInitial = data?.middleInitial ?? ''
    this.lastName = data?.lastName ?? ''
    this.tin = data?.tin ?? ''
    this.dateOfBirth = isNotNil(data?.dob) ? data.dob : null
    this.occupation = data?.occupation ?? ''
    this.filingStatus = data?.filingStatus ?? null
    this.phoneNumber = data?.phoneNumber ?? ''
    this.emailAddress = data?.emailAddress ?? ''
    this.address.reset(data?.contactAddress)

    this.validation.reset()
  }

  activate() {
    this.lifecycle.activate()
  }

  deactivate() {
    this.lifecycle.deactivate()
  }
}
